import { z } from "zod";
import { Email, IsoDate, IsoDateTime, LongText, MxPhone, NonEmptyString, PageQuery, partialWithoutDefaults, Sex, ShortText, Uuid } from "./common.js";
import { AnalyteKind } from "./catalogue.js";
import { CultureResult } from "./culture.js";

// Patients. Sensitive personal data under the LFPDPPP: the API returns them only inside the
// tenant's RLS context and every read of a result is chained (ADR 0011). Nothing here is ever a
// real person in fixtures or tests.

// -- name normalisation ------------------------------------------------------------------------
// The laboratory writes patient names in capitals without accents, as the birth certificate and
// the CURP do: mixed case, accents and stray spaces cause trouble with the civil registry (Very,
// 2026-11-02). Ñ stays: Mexican actas keep it. Every write of first_name, paternal_surname and
// maternal_surname goes through `normalizePatientName` — the command handlers, the people
// importer, the backfill CLI and the web form — so there is exactly one rule.

/** The names a patient record carries; the three fields `normalizePatientName` applies to. */
export const PATIENT_NAME_FIELDS = ["first_name", "paternal_surname", "maternal_surname"] as const;

/**
 * The stored form of one patient name part: capitals without accents ("Jazmín" → "JAZMIN",
 * "Güemes" → "GUEMES") but with Ñ ("Peña" → "PEÑA"), inner whitespace collapsed to one space,
 * trimmed. Idempotent: normalising a normalised value returns it unchanged.
 */
export function normalizePatientName(raw: string): string {
  return patientNameAsTyped(raw).replace(/\s+/gu, " ").trim();
}

/**
 * What a name field shows while the receptionist types: the same capitals-without-accents as the
 * stored form but without trimming or collapsing, so the space she is about to type survives the
 * keystroke.
 */
export function patientNameAsTyped(raw: string): string {
  // NFC first so a decomposed "n + combining tilde" becomes Ñ and survives; upper-case so ñ → Ñ;
  // then strip every combining mark from the text between the Ñs.
  return raw
    .normalize("NFC")
    .toLocaleUpperCase("es-MX")
    .split("Ñ")
    .map((part) => part.normalize("NFD").replace(/\p{M}/gu, "").normalize("NFC"))
    .join("Ñ");
}

export const Patient = z.object({
  id: Uuid,
  first_name: NonEmptyString,
  paternal_surname: NonEmptyString,
  maternal_surname: ShortText.nullable(),
  birth_date: IsoDate,
  sex: Sex,
  phone: z.string().nullable(),
  email: Email.nullable(),
  origin: ShortText.nullable(),
  notes: LongText.nullable(),
  created_at: IsoDateTime,
  /** Set when a duplicate was merged into another record. */
  merged_into: Uuid.nullable(),
});
export type Patient = z.infer<typeof Patient>;

export const PatientRegisterPayload = z.object({
  first_name: NonEmptyString,
  paternal_surname: NonEmptyString,
  maternal_surname: ShortText.nullable().default(null),
  birth_date: IsoDate,
  sex: Sex,
  phone: MxPhone.nullable().default(null),
  email: Email.nullable().default(null),
  /** "Lugar de procedencia" — asked at the counter (meeting 2026-10-31). */
  origin: ShortText.nullable().default(null),
  notes: LongText.nullable().default(null),
});
export type PatientRegisterPayload = z.infer<typeof PatientRegisterPayload>;

/** A patch: only the fields present are written (omitted fields keep their value, see `partialWithoutDefaults`). */
export const PatientUpdatePayload = partialWithoutDefaults(PatientRegisterPayload).extend({ patient_id: Uuid });
export type PatientUpdatePayload = z.infer<typeof PatientUpdatePayload>;

export const PatientMergePayload = z.object({
  /** The record that survives. */
  keep_patient_id: Uuid,
  /** The duplicate; its orders move to `keep_patient_id` and it is marked merged. */
  merge_patient_id: Uuid,
});
export type PatientMergePayload = z.infer<typeof PatientMergePayload>;

export const PatientListQuery = PageQuery.extend({
  /** Matches name fragments and the phone. */
  q: z.string().trim().max(120).optional(),
});
export type PatientListQuery = z.infer<typeof PatientListQuery>;

/** Compact row for the patient picker and the cached index (no results, ever). */
export const PatientSummary = Patient.pick({
  id: true,
  first_name: true,
  paternal_surname: true,
  maternal_surname: true,
  birth_date: true,
  sex: true,
  phone: true,
});
export type PatientSummary = z.infer<typeof PatientSummary>;

/** Visits, studies and validated results of one patient, newest first. Staff screen (ADR 0011: viewing it is an access event). */
export const PatientHistoryValue = z.object({
  analyte_code: z.string(),
  analyte_name: z.string(),
  /** `document` values are the formatted text of ADR 0019 (HTML subset). */
  kind: AnalyteKind,
  value_numeric: z.number().nullable(),
  value_text: z.string().nullable(),
  culture: CultureResult.nullable(),
  unit: z.string().nullable(),
  flag: z.enum(["N", "L", "H", "A"]).nullable(),
  interval_text: z.string().nullable(),
});

export const PatientHistoryItem = z.object({
  order_item_id: Uuid,
  study_clave: z.string(),
  study_name: z.string(),
  state: z.enum(["pending", "captured", "validated", "issued", "amended", "cancelled"]),
  validated_at: IsoDateTime.nullable(),
  /** Only present once validated; nothing unvalidated leaves the capture screen. */
  values: z.array(PatientHistoryValue),
});

/**
 * The PDF the history's print button opens: the order's newest final or amendment report
 * (the migrated Toronja PDFs are `final`). Partial reports are not offered here.
 */
export const PatientHistoryReport = z.object({
  id: Uuid,
  kind: z.enum(["final", "amendment"]),
  rendered_at: IsoDateTime,
  filename: z.string(),
  /** The order owes money: the PDF answers 402 unless a Químico or above asks for a staff view. */
  held_for_balance: z.boolean(),
});
export type PatientHistoryReport = z.infer<typeof PatientHistoryReport>;

export const PatientHistoryOrder = z.object({
  order_id: Uuid,
  folio: z.string(),
  branch_name: z.string(),
  occurred_at: IsoDateTime,
  state: z.string(),
  doctor_display: z.string(),
  total_cents: z.number().int(),
  balance_cents: z.number().int(),
  /** The order's observaciones (`clinical_notes`). */
  clinical_notes: z.string().nullable(),
  origin: z.enum(["app", "migrated"]),
  /** The previous system's folio, on migrated orders. */
  external_folio: z.string().nullable(),
  latest_report: PatientHistoryReport.nullable(),
  items: z.array(PatientHistoryItem),
});

export const PatientHistory = z.object({ patient: Patient, orders: z.array(PatientHistoryOrder) });
export type PatientHistory = z.infer<typeof PatientHistory>;
